/**
 * PHASE 3: Offline Queue Service
 * Central API for enqueueing and replaying offline actions
 */

import {
  addQueueItem,
  getPendingQueueItems,
  getQueueItemById,
  updateQueueItem,
  deleteQueueItem,
  findPendingByEntity,
  getQueueItemByIdempotencyKey,
  addSyncLog,
  generateUUID
} from './db';
import { shouldAttemptNetworkOperation, getNetworkStatus } from './network';
import { recordQueueEnqueued, recordQueueReplayCompleted, recordQueueReplayFailed, recordConflictDetected } from './metrics';

// Action types that can be deduplicated (collapsed into latest version)
const COLLAPSIBLE_TYPES = ['AVAILABILITY_UPDATE', 'SCHEDULE_ACK'];

// Action types that should NEVER be collapsed (each is a unique event)
const NON_COLLAPSIBLE_TYPES = ['TIMECLOCK_PUNCH'];

// Retry backoff configuration
const RETRY_BACKOFF_MS = {
  0: 0,           // immediate
  1: 30 * 1000,   // 30 seconds
  2: 2 * 60 * 1000,   // 2 minutes
  3: 5 * 60 * 1000,   // 5 minutes
  4: 15 * 60 * 1000,  // 15 minutes
  5: 30 * 60 * 1000   // 30 minutes (capped)
};

const MAX_RETRY_COUNT = 10;

/**
 * Compute next retry timestamp based on retry count
 * @param {number} retryCount
 * @returns {number} timestamp in ms
 */
export function computeNextRetryAt(retryCount) {
  const backoffMs = RETRY_BACKOFF_MS[Math.min(retryCount, 5)] || RETRY_BACKOFF_MS[5];
  return Date.now() + backoffMs;
}

/**
 * Build idempotency key for an action
 * @param {Object} item
 * @returns {string}
 */
export function buildIdempotencyKey(item) {
  // For timeclock punches, use unique key per punch event
  if (item.type === 'TIMECLOCK_PUNCH') {
    return `${item.type}:${item.userId}:${item.clientGeneratedAt}:${generateUUID().slice(0, 8)}`;
  }
  
  // For collapsible actions, use entity-based key (allows deduplication)
  if (item.entityType && item.entityId) {
    return `${item.type}:${item.entityType}:${item.entityId}:${item.userId}`;
  }
  
  // Default: unique key per request
  return `${item.type}:${item.clientGeneratedAt}:${generateUUID().slice(0, 8)}`;
}

/**
 * Check if action should collapse with existing pending action
 * @param {Object} newItem
 * @returns {Promise<{shouldCollapse: boolean, existingItem: Object|null}>}
 */
async function checkDedupe(newItem) {
  // Never collapse timeclock punches
  if (NON_COLLAPSIBLE_TYPES.includes(newItem.type)) {
    return { shouldCollapse: false, existingItem: null };
  }
  
  // Check for collapsible types
  if (COLLAPSIBLE_TYPES.includes(newItem.type) && newItem.entityType && newItem.entityId) {
    const existing = await findPendingByEntity(newItem.entityType, newItem.entityId);
    if (existing && existing.status === 'pending') {
      return { shouldCollapse: true, existingItem: existing };
    }
  }
  
  return { shouldCollapse: false, existingItem: null };
}

/**
 * Enqueue an API action for offline sync
 * @param {Object} input
 * @returns {Promise<{queued: boolean, item: Object, collapsed?: boolean}>}
 */
export async function enqueueApiAction(input) {
  const now = Date.now();
  
  const item = {
    type: input.type || 'GENERIC_API_ACTION',
    endpoint: input.endpoint,
    method: input.method || 'POST',
    payload: input.payload || {},
    headers: input.headers || {},
    entityType: input.entityType || null,
    entityId: input.entityId || null,
    userId: input.userId || null,
    merchantId: input.merchantId || null,
    priority: input.priority ?? 5,
    requiresAuth: input.requiresAuth ?? true,
    clientGeneratedAt: input.clientGeneratedAt || now
  };
  
  // Generate idempotency key
  item.idempotencyKey = input.idempotencyKey || buildIdempotencyKey(item);
  
  // Check for deduplication
  const { shouldCollapse, existingItem } = await checkDedupe(item);
  
  if (shouldCollapse && existingItem) {
    // Update existing pending item with new payload
    console.log(`[QueueService] Collapsing ${item.type} into existing item ${existingItem.id}`);
    const updated = await updateQueueItem(existingItem.id, {
      payload: item.payload,
      headers: item.headers,
      clientGeneratedAt: item.clientGeneratedAt,
      nextRetryAt: now,
      retryCount: 0,
      lastError: null
    });
    
    recordQueueEnqueued(item.type, true);
    return { queued: true, item: updated, collapsed: true };
  }
  
  // Add new queue item
  const queuedItem = await addQueueItem(item);
  
  recordQueueEnqueued(item.type, false);
  console.log(`[QueueService] Enqueued ${item.type}: ${queuedItem.id}`);
  
  // Request background sync if available
  requestBackgroundSync();
  
  return { queued: true, item: queuedItem, collapsed: false };
}

/**
 * Should an item be replayed now?
 * @param {Object} item
 * @param {number} now
 */
export function shouldReplayItem(item, now = Date.now()) {
  if (item.status !== 'pending') return false;
  if (item.nextRetryAt > now) return false;
  if (item.retryCount >= MAX_RETRY_COUNT) return false;
  return true;
}

/**
 * Replay pending queue items
 * @param {Object} options - { limit?, onProgress? }
 * @returns {Promise<{total: number, success: number, failed: number, conflicts: number}>}
 */
export async function replayPendingQueue(options = {}) {
  const { limit = 20, onProgress } = options;
  
  if (!shouldAttemptNetworkOperation()) {
    console.log('[QueueService] Skipping replay - network unavailable');
    return { total: 0, success: 0, failed: 0, conflicts: 0 };
  }
  
  const results = { total: 0, success: 0, failed: 0, conflicts: 0 };
  const pendingItems = await getPendingQueueItems(limit);
  results.total = pendingItems.length;
  
  if (pendingItems.length === 0) {
    return results;
  }
  
  console.log(`[QueueService] Replaying ${pendingItems.length} pending items...`);
  
  for (const item of pendingItems) {
    const startTime = Date.now();
    
    // Mark as syncing
    await updateQueueItem(item.id, { status: 'syncing' });
    
    try {
      // Build headers with idempotency key
      const headers = {
        'Content-Type': 'application/json',
        'x-idempotency-key': item.idempotencyKey,
        'x-client-generated-at': item.clientGeneratedAt.toString(),
        'x-offline-replay': 'true',
        ...item.headers
      };
      
      // Add auth token if required
      if (item.requiresAuth) {
        const token = getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      // Make the request
      const response = await fetch(item.endpoint, {
        method: item.method,
        headers,
        body: JSON.stringify(item.payload)
      });
      
      const durationMs = Date.now() - startTime;
      
      // Handle response
      if (response.ok) {
        // Success
        await markQueueItemCompleted(item.id, {
          statusCode: response.status,
          durationMs
        });
        results.success++;
        onProgress?.({ item, result: 'success' });
      } else if (response.status === 409) {
        // Conflict
        let conflictData = null;
        try {
          conflictData = await response.json();
        } catch (e) {}
        
        await markQueueItemConflict(item.id, {
          statusCode: response.status,
          message: conflictData?.message || 'Conflict detected',
          serverState: conflictData
        });
        results.conflicts++;
        recordConflictDetected(item.type);
        onProgress?.({ item, result: 'conflict' });
      } else if (response.status === 401 || response.status === 403) {
        // Auth error - leave as pending for now
        await markQueueItemFailed(item.id, `Auth error: ${response.status}`, false);
        results.failed++;
        onProgress?.({ item, result: 'auth_error' });
      } else if (response.status === 422) {
        // Validation error - mark as failed (no retry)
        await markQueueItemFailed(item.id, `Validation error: ${response.status}`, true);
        results.failed++;
        onProgress?.({ item, result: 'validation_error' });
      } else {
        // Server error - retry
        await markQueueItemFailed(item.id, `Server error: ${response.status}`, false);
        results.failed++;
        onProgress?.({ item, result: 'retry' });
      }
      
    } catch (error) {
      // Network error - retry
      console.error(`[QueueService] Replay failed for ${item.id}:`, error);
      await markQueueItemFailed(item.id, error.message, false);
      results.failed++;
      onProgress?.({ item, result: 'network_error' });
    }
  }
  
  console.log(`[QueueService] Replay complete:`, results);
  return results;
}

/**
 * Mark queue item as completed
 * @param {string} id
 * @param {Object} responseMeta
 */
export async function markQueueItemCompleted(id, responseMeta = {}) {
  const item = await getQueueItemById(id);
  if (!item) return;
  
  await updateQueueItem(id, { status: 'completed' });
  
  await addSyncLog({
    queueItemId: id,
    type: item.type,
    statusCode: responseMeta.statusCode,
    result: 'success',
    message: 'Synced successfully',
    durationMs: responseMeta.durationMs
  });
  
  recordQueueReplayCompleted(item.type);
}

/**
 * Mark queue item as failed
 * @param {string} id
 * @param {string} error
 * @param {boolean} permanent - if true, mark as failed immediately
 */
export async function markQueueItemFailed(id, error, permanent = false) {
  const item = await getQueueItemById(id);
  if (!item) return;
  
  const newRetryCount = item.retryCount + 1;
  const shouldFail = permanent || newRetryCount >= MAX_RETRY_COUNT;
  
  await updateQueueItem(id, {
    status: shouldFail ? 'failed' : 'pending',
    retryCount: newRetryCount,
    nextRetryAt: shouldFail ? null : computeNextRetryAt(newRetryCount),
    lastError: error
  });
  
  await addSyncLog({
    queueItemId: id,
    type: item.type,
    result: shouldFail ? 'failed' : 'retry',
    message: error
  });
  
  if (shouldFail) {
    recordQueueReplayFailed(item.type);
  }
}

/**
 * Mark queue item as conflict
 * @param {string} id
 * @param {Object} conflictInfo
 */
export async function markQueueItemConflict(id, conflictInfo) {
  const item = await getQueueItemById(id);
  if (!item) return;
  
  await updateQueueItem(id, {
    status: 'conflict',
    lastError: conflictInfo.message
  });
  
  await addSyncLog({
    queueItemId: id,
    type: item.type,
    statusCode: conflictInfo.statusCode,
    result: 'conflict',
    message: conflictInfo.message
  });
}

/**
 * Retry a specific failed/conflict item
 * @param {string} id
 */
export async function retryQueueItem(id) {
  await updateQueueItem(id, {
    status: 'pending',
    retryCount: 0,
    nextRetryAt: Date.now(),
    lastError: null
  });
}

/**
 * Discard a queue item
 * @param {string} id
 */
export async function discardQueueItem(id) {
  await deleteQueueItem(id);
}

// ==================== HELPERS ====================

/**
 * Get auth token from localStorage
 */
function getAuthToken() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('staff_token') || 
         localStorage.getItem('backoffice_token') ||
         localStorage.getItem('auth_token');
}

/**
 * Request background sync if supported
 */
function requestBackgroundSync() {
  if (typeof navigator !== 'undefined' && 
      'serviceWorker' in navigator && 
      'sync' in window.SyncManager) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.sync.register('asterpos-sync-queue').catch(() => {
        // Background sync not available
      });
    });
  }
}

// ==================== CONVENIENCE METHODS FOR SPECIFIC ACTION TYPES ====================

/**
 * Queue a time clock punch. Punches have their own queue (lib/timeclock/punchQueue: the
 * device time + a client punch id, sent to /api/timeclock/v2/punches/sync, applied in order,
 * idempotent); this generic queue only hands it over.
 * data: { action ('CLOCK_IN' | 'BREAK_START' | 'BREAK_END' | 'CLOCK_OUT'), staffId,
 *         restaurantId, auth ('staff' by default), breakPaid, roleId, staffName, phone, pin }
 */
export async function queueTimeclockPunch(data) {
  const { queuePunch } = await import('@/lib/timeclock/punchQueue');
  return queuePunch({
    auth: data.auth || 'staff', restaurantId: data.restaurantId, action: String(data.action || '').toUpperCase(),
    breakPaid: !!data.breakPaid, roleId: data.roleId || null,
    who: { staffId: data.staffId ?? null, staffName: data.staffName || null, phone: data.phone || null, pin: data.pin || null },
  });
}

/**
 * Queue an availability update
 */
export async function queueAvailabilityUpdate(data) {
  return enqueueApiAction({
    type: 'AVAILABILITY_UPDATE',
    endpoint: '/api/availability/me',
    method: 'PUT',
    payload: data.availability,
    entityType: 'availability',
    entityId: data.staffId, // Same entity ID allows collapsing
    userId: data.staffId,
    merchantId: data.restaurantId,
    priority: 3
  });
}

/**
 * Queue a schedule acknowledgement
 */
export async function queueScheduleAck(data) {
  return enqueueApiAction({
    type: 'SCHEDULE_ACK',
    endpoint: '/api/labor/shifts/acknowledge',
    method: 'POST',
    payload: { shift_id: data.shiftId, action: data.action || 'acknowledge', notes: data.notes || null },
    entityType: 'schedule',
    entityId: data.shiftId, // Same shift allows collapsing
    userId: data.staffId,
    merchantId: data.restaurantId,
    priority: 2
  });
}

export default {
  enqueueApiAction,
  replayPendingQueue,
  markQueueItemCompleted,
  markQueueItemFailed,
  markQueueItemConflict,
  retryQueueItem,
  discardQueueItem,
  computeNextRetryAt,
  shouldReplayItem,
  buildIdempotencyKey,
  queueTimeclockPunch,
  queueAvailabilityUpdate,
  queueScheduleAck
};
